import { LitElement, broadcastReceiver, html, utils } from '@hh/global.js'

class HHHeader extends LitElement {
  EXIT_TIMEOUT = 500

  static properties = {
    promoBar: { state: true }
  }

  constructor() {
    super()

    this.triggers = this.querySelectorAll('.js-header-mega-menu-trigger')
    this.menuItems = this.querySelectorAll('.js-header-mega-menu-panel')

    this.openMenu = this.openMenu.bind(this)
    this.closeMenu = this.closeMenu.bind(this)
    this.handleMenuItemEnter = this.handleMenuItemEnter.bind(this)
    this.handleMenuItemLeave = this.handleMenuItemLeave.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    this.initPromoBar()
    this.handleHashedLinks()

    this.receiver = broadcastReceiver('product-added', (...args) => {
      this.openCart()
    })

    for (const trigger of this.triggers) {
      trigger.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.closeMenu(event)
      })
      trigger.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.openMenu(event)
      })
      trigger.addEventListener('pointerup', this.openMenu)
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        window.location.href = trigger.getAttribute('data-url');
      })
    }

    for (const menuItem of this.menuItems) {
      menuItem.addEventListener('pointerenter', this.handleMenuItemEnter)
      menuItem.addEventListener('pointerleave', this.handleMenuItemLeave)
    }

    // Add keyboard listener for Escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === "Escape") {
        this.closeMenu(); // Assuming closeMenu can handle being called without an event object
      }
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()

    for (const trigger of this.triggers) {
      trigger.removeEventListener('pointerleave', this.closeMenu)
      trigger.removeEventListener('pointerenter', this.openMenu)
      trigger.removeEventListener('pointerup', this.openMenu)
    }

    for (const menuItem of this.menuItems) {
      menuItem.removeEventListener('pointerenter', this.handleMenuItemEnter)
      menuItem.removeEventListener('pointerleave', this.handleMenuItemLeave)
    }
  }

  clearExitTimer() {
    try {
      clearTimeout(this.navLeaveTimer)
      this.navLeaveTimer = null
    } catch (error) {
      console.error(error)
    }
  }

  setExitTimer() {
    try {
      this.navLeaveTimer = setTimeout(() => {
        this.activeMenuItem = null
        this.applyState()
      }, this.EXIT_TIMEOUT)
    } catch (error) {
      console.error(error)
    }
  }

  openMenu(event) {
    try {
      event.preventDefault()
      this.clearExitTimer()
      this.activeMenuItem = event.currentTarget?.closest('.header__menu-item--parent-wrapper')?.querySelector('.js-header-mega-menu-panel') || null
    } catch (error) {
      this.activeMenuItem = null
    } finally {
      this.applyState()
    }
  }

  closeMenu(event) {
    if (event) { event.preventDefault() }
    this.setExitTimer();
  }

  handleMenuItemEnter() {
    this.clearExitTimer()
  }

  handleMenuItemLeave() {
    this.setExitTimer()
  }

  applyState() {
    for (const menuItem of this.menuItems) {
      if (this.activeMenuItem === menuItem) {
        menuItem.classList.add('active')
        menuItem.setAttribute('aria-hidden', false)
        menuItem.setAttribute('aria-expanded', true)
      } else {
        menuItem.classList.remove('active')
        menuItem.setAttribute('aria-hidden', true)
        menuItem.setAttribute('aria-expanded', false)
      }
    }
  }

  closeOpenModals(e) {
    utils.all('hh-details-modal', this).map((element) => {
      if (element !== e.detail.value) {
        element.setAttribute('open', false)
      }
    })

    utils.all('header-search-details', this).map((element) => {
      if (element !== e.detail.value) {
        element.removeAttribute('open')
      }
    })

    utils.all('hh-header-submenu', this).map((element) => {
      if (element !== e.detail.value) {
        element.closeSubmenu()
      }
    })


    utils.all('hh-explore-collections-mobile', this).map((element) => {
      if (element !== e.detail.value) {
        element?.resetSideMenu()
      }
    })
  }

  toggleCart(e) {
    e?.detail?.event?.preventDefault()
    utils.toggle(document.body, ['mini-cart-is-active'])
  }

  openCart () {
    setTimeout(() => {
      document.body.classList.add('mini-cart-is-active')
    }, 500);
  }

  toggleAccount(e) {
    e?.detail?.event?.preventDefault()
    utils.toggle(document.body, ['account-header-modal-is-active'])
  }

  initPromoBar() {
    this.promoBar = document.querySelector('.promo-bar')
    if (this?.promoBar?.hasChildNodes()) {

      const promoCloseButton = this.promoBar.querySelector('button')
      if (sessionStorage.getItem('promoBarDismissed')) {
        this.promoBar.remove()
      } else {
        promoCloseButton.addEventListener('click', () => {
          this.promoBar.style.opacity = '0';
          setTimeout(() => {
            this.dismissPromoBar()
            const stickyHeader = document.querySelector('hh-sticky-header')
            stickyHeader.doSettings()
          }, 500);
        });
      }
    }
  }

  dismissPromoBar() {
    this.promoBar.remove()
    sessionStorage.setItem('promoBarDismissed', true);
  }

  handleHashedLinks() {
    const hashedLinks = this.querySelectorAll('a[href="#"]')

    hashedLinks?.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault()
      })
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    clearTimeout(this.dismissPromoBar)
  }

  render() {
    return html`<slot
      @modalOpened="${this.closeOpenModals}"
      @toggleCart="${this.toggleCart}"
      @toggleAccount="${this.toggleAccount}"
    ></slot>`
  }
}

customElements.define('hh-header', HHHeader)

class HHHeaderSubmenu extends LitElement {
  constructor() {
    super()

    this.detailsEl = this.querySelector('details')
    this.summaryEl = this.querySelector('summary')
    this.childEl = this.querySelector('.mobile-menu__submenu')
    this.closeButtonEl = this.querySelector('[data-close-details]')
    this.opened = false
  }

  connectedCallback() {
    super.connectedCallback()
    this.summaryEl?.addEventListener('click', this.handleSummaryClick.bind(this))
    this.closeButtonEl?.addEventListener('click', this.closeSubmenu.bind(this))
  }

  handleSummaryClick() {
    setTimeout(() => {
      this.childEl.removeAttribute('inert')
      this.detailsEl.classList.add('menu-opening')
      setTimeout(() => {
        this.opened = true
        this.querySelector('[data-close-details]')?.focus()
      }, 5000);
    }, 100)
  }

  closeSubmenu() {
    this.detailsEl.removeAttribute('open');
    this.detailsEl.classList.remove('menu-opening');
    this.childEl.setAttribute('inert', true)
    this.opened = false
  }

  render() {
    return html`
      <slot name="summary"></slot>
      <hh-focus-trap active="${this.opened}">
        <slot name="details"></slot>
      </hh-focus-trap>
    `
  }
}

customElements.define('hh-header-submenu', HHHeaderSubmenu)
